import React from 'react';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';

// @material
import { withStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';

// core
import { useGlobal } from '_core/hooks/useGlobal';
import { withLang } from '_core/hocs/withLang';
import { withCatch } from '_core/hocs/withCatch';
import { useAnalyticsContext } from '_core/contexts/Analytics';
import { customCallback } from '_core/utils/customCallback';
import { getFieldOptions } from '_core/utils/getFieldOptions';
import { RegisterFormView } from './RegisterFormView';


const RawRegisterForm = (props) => {
  const {
    parentLogin,
    onSuccess,
    onFailure,
    successMessage,
    hasSuccessMessage,
    hasFailureMessage,
    isParentHidden,
    fieldsOrder,
    fieldsOptions,
    hasParentName,
    hasParentSwitch,
    getLangString: l
  } = props;

  const {
    notify: { enqueueSnackbar },
    request: { queue },
    config: {
      API_METHOD,
      REGISTER_FIELD,
      // CLIENT_AGE_MAX,
      // CLIENT_AGE_MIN,
      SITE_ANALYTICS_FORMS
    },
  } = useGlobal();

  const theme = useTheme();
  const [cookies] = useCookies(['parent']);
  
  const analytics = useAnalyticsContext();

  const parent = React.useMemo(() => {
    try {
      return parentLogin || decodeURIComponent(cookies['parent'].replace(/\-/g, '%'));
    } catch (_) {
      return cookies['parent'];
    }
  }, [cookies['parent']]);

  const [values, setValues] = React.useState({ parent });
  const [canSubmit, setCanSubmit] = React.useState(false);
  const [allChecked, setAllChecked] = React.useState(false);
  const [hasParent, setHasParent] = React.useState(!isParentHidden);
  const registerCallback = customCallback(theme.pages.goalCallback, 'REGISTER');

  const hiddenFields = isParentHidden ? { parent } : undefined;
  const fieldNames = Object.values(REGISTER_FIELD);
  const rawHasParentSwitch = !!hasParentSwitch && parent == null;

  const method =
    API_METHOD.VISITOR_CREATE +
    (rawHasParentSwitch && !hasParent ? '_no_parent' : '');

  const getOnChange = (field) => (value) => {
    setValues(oldValues => ({
      ...oldValues,
      [field]: value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault()
    registerCallback()
    let newInfo = {
      ...values,
      ...(values.birth_date
        ? {
            birth_date:
              typeof values.birth_date === 'string'
                ? values.birth_date
                : values.birth_date.format('DD.MM.YYYY'),
          }
        : {}),
      ...(values.locality
        ? {
            locality: values.locality?.id,
          }
        : {})
    }

    try {
      const result = await queue(API_METHOD.VISITOR_CREATE, {
        form: method,
        ...newInfo,
      });

      hasSuccessMessage &&
        enqueueSnackbar(l(successMessage), { variant: 'success' });

      typeof onSuccess === 'function' && onSuccess({ values, result });
      
      if (SITE_ANALYTICS_FORMS.visitorCreate) {
        analytics.form({
          form: method,
          name: [newInfo.lastname, newInfo.firstname, newInfo.patronymic].filter(Boolean).join(' ') || newInfo.login || result.id,
          phone: result.phone || result.mobile,
          email: result.email,
          
          ...SITE_ANALYTICS_FORMS.visitorCreate
        });
      }
    } catch (error) {
      process.env.NODE_ENV !== 'production' &&
        hasFailureMessage &&
        enqueueSnackbar(error.message, { variant: 'error' });

      typeof onFailure === 'function' && onFailure({ values, error });
    }
  };

  // const now = Date.now()

  const getFieldOnChangeSave = () => (value) => {
  };

  const fields = fieldsOrder.reduce((result, name) => {
    if (name === 'parent' && !hasParent) {
      return result;
    }

    if (fieldNames.includes(name)) {
      result[name] = getFieldOptions({
        name,
        value: values[name],
        label: l(`r._.guest.register.form.${name}.label`),
        onChange: getOnChange(name),
        onChangeSave: getFieldOnChangeSave(name),
        options: fieldsOptions[name],
      });
    }

    return result;
  }, {});

  return (
    <RegisterFormView
      method={method}
      fields={fields}
      parentLogin={parentLogin}
      hiddenFields={hiddenFields}
      fieldsOrder={fieldsOrder}
      fieldsOptions={fieldsOptions}
      onSubmit={onSubmit}
      setCanSubmit={setCanSubmit}
      setAllChecked={setAllChecked}
      submitDisabled={!canSubmit || !allChecked}
      hasParent={hasParent}
      hasParentName={hasParentName}
      parentCookie={parent}
      hasParentSwitch={rawHasParentSwitch}
      onParentToggle={() => setHasParent(v => !v)}
    />
  );
};

RawRegisterForm.defaultProps = {
  hasSuccessMessage: true,
  hasFailureMessage: true,
  successMessage: 'r._.register.text.success',
};

RawRegisterForm.propTypes = {
  // self props
  fieldsOptions: PropTypes.object,
  fieldsOrder: PropTypes.arrayOf(PropTypes.string),
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  isParentHidden: PropTypes.bool,
  hasParentName: PropTypes.bool,
  hasParentSwitch: PropTypes.bool,
  hasSuccessMessage: PropTypes.bool,
  hasFailureMessage: PropTypes.bool,
  successMessage: PropTypes.string,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
};

export const RegisterForm = withCatch(
  withLang(withStyles({}, { name: 'RtsRegisterForm' })(RawRegisterForm))
);
